import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PollView } from '../../shared/polls';
import { Markdown } from '../lib/Markdown';
import { Meldung, type MeldungsInhalt } from '../lib/Meldung';
import type { Announcement } from '../lib/useAnnouncements';
import { PollCard } from './PollCard';

const dateFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' });

/**
 * Overlay mit allen Ankündigungen. Wird vom Strip **und** vom News-Panel
 * geöffnet, deshalb von außen gesteuert.
 *
 * Abfragen (Design-System §6): offene oben, damit niemand sie übersieht;
 * beendete unter den Ankündigungen.
 *
 * Admins sehen am Ende Links zum Anschlagen. Nur Sichtbarkeit – ob jemand
 * anschlagen darf, entscheiden die Admin-Functions.
 */
export function BoardSheet({
  items,
  polls = [],
  onPollsChanged,
  isAdmin = false,
  open,
  onClose,
}: {
  items: Announcement[] | null;
  polls?: PollView[];
  onPollsChanged?: () => Promise<void>;
  isAdmin?: boolean;
  open: boolean;
  onClose: () => void;
}) {
  const [meldung, setMeldung] = useState<MeldungsInhalt | null>(null);
  const meldungSchliessen = useCallback(() => setMeldung(null), []);
  const offen = polls.filter((p) => p.open);
  const beendet = polls.filter((p) => !p.open);

  const pollCard = (poll: PollView) => (
    <PollCard
      key={poll.id}
      poll={poll}
      onAnswered={async (text) => {
        await onPollsChanged?.();
        setMeldung({ art: 'success', text });
      }}
      onError={(text) => setMeldung({ art: 'error', text })}
    />
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Schwarzes Brett"
      onClick={onClose}
    >
      <div className="sheet__panel" onClick={(event) => event.stopPropagation()}>
        <div className="sheet__head">
          <h2>// schwarzes brett</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>

        {offen.map(pollCard)}

        {items && items.length > 0 ? (
          items.map((item) => (
            <article className="note" key={item.id}>
              <div className="note__head">
                <h3 className="note__title">{item.title}</h3>
                {item.pinned && <span className="badge-pill badge-pill--pink">angepinnt</span>}
              </div>
              <p className="note__meta">
                {dateFormat.format(new Date(item.created_at))}
                {item.valid_until
                  ? ` · gültig bis ${dateFormat.format(new Date(item.valid_until))}`
                  : ''}
              </p>
              <div className="note__body">
                <Markdown source={item.body_md} />
              </div>
            </article>
          ))
        ) : polls.length === 0 ? (
          <div className="empty">Am Schwarzen Brett ist gerade nichts angeschlagen.</div>
        ) : null}

        {beendet.map(pollCard)}

        {isAdmin && (
          <div className="sheet__admin">
            <Link className="px-btn px-btn--ghost px-btn--sm" to="/admin?bereich=brett">
              + Ankündigung
            </Link>
            <Link className="px-btn px-btn--ghost px-btn--sm" to="/admin?bereich=abfragen">
              + Abfrage
            </Link>
          </div>
        )}
      </div>
      {/* Klicks auf die Meldung sollen das Brett nicht schließen. */}
      <div onClick={(event) => event.stopPropagation()}>
        <Meldung inhalt={meldung} onClose={meldungSchliessen} />
      </div>
    </div>
  );
}
